<script setup lang="ts">
type Emits = {
  'update-games-list': [any[]];
  'update-pagination': [Record<string, any>];
};

const emit = defineEmits<Emits>();

interface FetchGamesParams {
  moreGames?: boolean;
  changeProvider?: boolean;
  changeCategory?: boolean;
}

const route: any = useRoute();
const { t } = useTexts();
const { isMobile } = useLayout();
const { getGames } = useCasino();
const { isLoading, isLoadingMoreGames } = storeToRefs(useCasinoStore());

const searchTimeout = ref<any>(null);
const isFiltersActive = ref<boolean>(false);
const isSearchActive = ref<boolean>(false);

const selectedCategories = ref<string[]>([]);
const selectedProviders = ref<string[]>([]);
const categoriesList = ref<any[]>([]);
const providersList = ref<any[]>([]);
const noSearchResults = ref<boolean>(false);
const searchQuery = ref<string>('');
const page = ref<number>(1);

const gamesObject = ref<any>({});

const categorySlug = computed<string>(() => {
  const { category } = route.params;

  if (Array.isArray(category)) {
    const [categorySlug] = category;

    return String(categorySlug).toLowerCase();
  } else if (category) {
    return String(category).toLowerCase();
  }

  return '';
});

const categoriesName = computed<string>(() => {
  const currentCategories = categoriesList.value.filter((category) => {
    const categorySlug = String(category.slug).toLowerCase();

    return selectedCategories.value.some((selectedCategory) => {
      return categorySlug === String(selectedCategory).toLowerCase();
    });
  });

  const currentCategoriesNames = currentCategories.map((selectedCategories) => {
    return selectedCategories.name;
  });

  if (currentCategoriesNames.length) {
    return currentCategoriesNames.join(', ');
  }

  return '';
});

const providerSlug = computed<string>(() => {
  const { provider } = route.params;

  if (provider) {
    return String(provider).toLowerCase();
  }

  return '';
});

const providersName = computed<string>(() => {
  const currentProviders = providersList.value.filter((provider) => {
    const providerSlug = String(provider.slug).toLowerCase();

    return selectedProviders.value.some((selectedProvider) => {
      return providerSlug === String(selectedProvider).toLowerCase();
    });
  });

  const currentProvidersNames = currentProviders.map((selectedProviders) => {
    return selectedProviders.name;
  });

  if (currentProvidersNames.length) {
    return currentProvidersNames.join(', ');
  }

  return '';
});

const sortBy = computed<string>(() => {
  const { category: params } = route.params;

  if (Array.isArray(params) && params.length > 1) {
    const [, sortOrderParam] = params;

    return String(sortOrderParam).toLowerCase();
  }

  return '';
});

const sortName = computed<string>(() => {
  if (sortBy.value === 'newest') {
    return t('casino.game.header.newest');
  }

  if (sortBy.value === 'popular') {
    return t('casino.game.header.popular');
  }

  return '';
});

const title = computed<string>(() => {
  const allGamesText = t('casino.game.header.all_games');
  const fromText = t('casino.game.header.from');
  const inText = t('casino.game.header.in');

  const message: string[] = [allGamesText];

  if (sortName.value) {
    message.push(sortName.value);
  }

  if (categoriesName.value) {
    // Sort categories //
    const categoryTranslated = t(`${categoriesName.value}`);

    message.push(`${inText} <h1>${categoryTranslated}</h1>`);
  }

  if (providersName.value) {
    message.push(`${fromText} <h1>${providersName.value}</h1>`);
  }

  return message.join(' ');
});

function isSlugValid(slug: string): boolean {
  return !!(slug && slug !== 'all');
}

const activeFilters = () => {
  isFiltersActive.value = !isFiltersActive.value;
  if (isFiltersActive.value) {
    isSearchActive.value = false;
  }
};

const activeSearch = () => {
  isSearchActive.value = !isSearchActive.value;
  if (isSearchActive.value) {
    isFiltersActive.value = false;
  }
};

function onFetchGamesHandle(data: any, options: FetchGamesParams) {
  const { changeProvider, changeCategory } = options;

  const paginationData = {
    currentPage: data.current_page,
    from: data.from,
    to: data.to,
    total: data.total,
    lastPage: data.last_page,
  };

  emit('update-pagination', paginationData);

  if (!categoriesList.value.length && !providersList.value.length) {
    categoriesList.value = data.categories;
    providersList.value = data.providers;
  } else if (searchQuery.value.length < 1) {
    noSearchResults.value = false;
  } else if (searchQuery.value) {
    if (!data.data.length) {
      noSearchResults.value = true;
    } else {
      noSearchResults.value = false;
      categoriesList.value =
        selectedCategories.value.length > 0 &&
        (changeCategory || changeProvider)
          ? categoriesList.value
          : data.categories;
      providersList.value =
        selectedProviders.value.length > 0 && (changeCategory || changeProvider)
          ? providersList.value
          : data.providers;
    }
  } else if (changeCategory) {
    providersList.value = data.providers;
    categoriesList.value =
      selectedCategories.value.length > 0
        ? categoriesList.value
        : data.categories;
  } else if (changeProvider) {
    categoriesList.value = data.categories;
    providersList.value =
      selectedProviders.value.length > 0 ? providersList.value : data.providers;
  } else {
    categoriesList.value =
      selectedCategories.value.length > 0
        ? categoriesList.value
        : data.categories;
    providersList.value =
      selectedProviders.value.length > 0 ? providersList.value : data.providers;
  }

  const isProviderValid = providersList.value.some((provider) => {
    return String(provider.slug).toLowerCase() === providerSlug.value;
  });

  const isCategoryValid = categoriesList.value.some((category) => {
    return String(category.slug).toLowerCase() === categorySlug.value;
  });

  if (!isProviderValid && !isCategoryValid && categorySlug.value !== 'all') {
    navigateTo(localeLink('/'));
  }

  const catSeoName = computed(() => {
    if (categorySlug.value === 'all') {
      return 'Todos os jogos de cassino';
    }

    return `Jogos de ${categoriesName.value}`;
  });

  const title = ref(
    `${catSeoName.value || providersName.value} | ${appName()} | Cassino Online | Casa de Apostas`,
  );

  const description = ref(
    `${catSeoName.value || providersName.value} | ${appName()} | Cassino Online | Casa de Apostas`,
  );

  if (isProviderValid) {
    title.value = `${providersName.value} 🍀 ${appName()} | Cassino Online | Casa de Apostas`;

    description.value = `Jogue na ${providersName.value} agora na ${appName()} 🎰 Cassino Online | Casa de Apostas`;
  }

  if (isCategoryValid || categorySlug.value === 'all') {
    title.value = `${catSeoName.value} 🍀 ${appName()} | Cassino Online | Casa de Apostas`;

    description.value = `${catSeoName.value} disponíveis para jogar na ${appName()} 🎰 Cassino Online | Casa de Apostas`;
  }

  useSeo({
    title: title.value,
    description: description.value,
  });
}

function fetchGames({
  moreGames = false,
  changeProvider = false,
  changeCategory = false,
}: FetchGamesParams = {}) {
  if (moreGames) {
    page.value++;
  }

  page.value = moreGames ? page.value : 1;

  const filterParams = [
    ...selectedCategories.value.map((e: any) => ['categories[]', e]),
    ...selectedProviders.value.map((e: any) => ['providers[]', e]),
    ['sort', sortBy.value],
    ['per_page', route.query.per_page || '24'],
    ['page', page.value],
    ['term', searchQuery.value || ''],
  ];

  if (!moreGames) {
    isLoading.value = true;
  } else {
    isLoadingMoreGames.value = true;
  }

  getGames(filterParams)
    .then((response) => {
      const newData = moreGames
        ? [...gamesObject.value.data, ...response.data]
        : response.data;

      gamesObject.value = { ...response, data: newData };

      emit('update-games-list', gamesObject.value);

      onFetchGamesHandle(response, { changeCategory, changeProvider });
    })
    .catch((error) => {
      console.error(error);

      gamesObject.value = [];
    })
    .finally(() => {
      isLoading.value = false;
      isLoadingMoreGames.value = false;
    });
}

watch(searchQuery, () => {
  isLoading.value = true;
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
  searchTimeout.value = setTimeout(() => {
    fetchGames();
  }, 1500);
});

watch(
  () => selectedCategories.value,
  () => {
    fetchGames({ changeCategory: true });
  },
);

watch(
  () => selectedProviders.value,
  () => {
    fetchGames({ changeProvider: true });
  },
);

watch(
  () => route,
  (to) => {
    if (to.name === 'games') {
      categoriesList.value = [];
      providersList.value = [];
      isLoading.value = true;
    }
  },
);

defineExpose({ fetchGames });

onMounted(() => {
  if (!isMobile.value) {
    isFiltersActive.value = true;
  }

  if (isSlugValid(categorySlug.value)) {
    selectedCategories.value = [categorySlug.value];
  } else if (isSlugValid(providerSlug.value)) {
    selectedProviders.value = [providerSlug.value];
  } else {
    fetchGames();
  }
});
</script>

<template>
  <header :class="$style.CasinoGamesHeader">
    <div :class="$style.header">
      <button
        :class="$style.backButton"
        @click="$router.back()"
      >
        <NuxtIcon name="arrows/arrow_left" />
      </button>

      <div
        :class="$style.title"
        v-html="title"
      />

      <div :class="$style.searchWrapper">
        <button @click="activeFilters()">
          <NuxtIcon
            v-if="!isFiltersActive"
            name="filter"
          />
          <NuxtIcon
            v-else
            name="times_circle"
          />
        </button>

        <button @click="activeSearch()">
          <NuxtIcon
            v-if="!isSearchActive"
            name="search"
          />
          <NuxtIcon
            v-else
            name="times_circle"
          />
        </button>
      </div>
    </div>

    <SectionsFilterItems
      v-model:search-query="searchQuery"
      v-model:selected-categories="selectedCategories"
      v-model:selected-providers="selectedProviders"
      :categories-list="categoriesList"
      :class="$style.filter"
      :is-filters-active="isFiltersActive"
      :is-mobile="isMobile"
      :is-search-active="isSearchActive"
      :providers-list="providersList"
    />
  </header>
  <div
    v-if="noSearchResults && !isLoading"
    class="alert alert-warning"
  >
    <span>
      {{ $t('components.search_bar.no_results') }}
    </span>
  </div>
</template>

<style module lang="scss">
.CasinoGamesHeader {
  @apply mt-1 mb-6 sm:mb-2 md:mt-1 md:mb-6 lg:flex items-center lg:justify-between xl:mt-4 xl:mb-10 xl:pb-1;
}

.header {
  @apply flex justify-start items-center text-left md:mb-3 lg:mb-0 lg:pr-2;
}

.backButton {
  @apply flex items-center bg-transparent text-texts/60;

  svg {
    @apply flex-none;
  }
}

.title {
  @apply inline ml-3 text-[.9rem] text-texts font-semibold;

  h1 {
    @apply inline font-bold;
  }
}

.searchWrapper {
  @apply flex md:hidden ml-auto;

  button {
    @apply py-2.5 px-2 first:ml-3 last:mr-1;
  }

  svg {
    @apply w-4 h-4 text-texts/60;
  }
}

.filter {
  @apply lg:flex-none lg:w-[fit] xl:w-[fit];
}
</style>
